/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { type FC, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Dialog } from "@components";
import { IOrderProduct } from "../../../../types";

export interface PublicProductModalProp {
  data: Partial<IOrderProduct>;
  isShowing: boolean;
  onConfirm: (data: Partial<IOrderProduct>) => void;
  onCancel: () => void;
  config?: any;
}

export const PublicProductForm: FC<
  Omit<PublicProductModalProp, "isShowing" | "onCancel">
> = ({ data, onConfirm }) => {
  const { t } = useTranslation(["order_form"]);
  const [formData, setFormData] = useState<Partial<IOrderProduct>>(data);
  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const [hasSubmitted, setHasSubmitted] = useState(false);

  useEffect(() => {
    setFormData(data);
  }, [data]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, boolean> = {};

    if (!formData.descriptionShort || formData.descriptionShort.trim() === "") {
      newErrors.descriptionShort = true;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type } = e.target;
    const finalValue =
      type === "number"
        ? value === ""
          ? undefined
          : parseFloat(value)
        : value;
    setFormData((prev) => ({ ...prev, [name]: finalValue }));

    // Clear error when user starts typing
    if (hasSubmitted) {
      setErrors((prev) => ({ ...prev, [name]: false }));
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setHasSubmitted(true);

    if (validateForm()) {
      onConfirm(formData);
    }
  };

  const inputStyle = (fieldName: string) => {
    const baseClass =
      "mt-1 block w-full rounded-md shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white";
    const errorClass = "border-red-500 focus:border-red-500 focus:ring-red-500";
    const normalClass = "border-border-strong";

    return `${baseClass} ${errors[fieldName] ? errorClass : normalClass}`;
  };

  const labelStyle = (fieldName: string) => {
    const baseClass = "block text-sm font-medium";
    const errorClass = "text-red-600";
    const normalClass = "text-secondary";

    return `${baseClass} ${errors[fieldName] ? errorClass : normalClass}`;
  };

  return (
    <form id="public-product-form" onSubmit={handleFormSubmit}>
      <div>
        <label
          htmlFor="descriptionShort"
          className={labelStyle("descriptionShort")}
        >
          {t("public_form.common.cargo_description")} *
        </label>
        <input
          type="text"
          name="descriptionShort"
          id="descriptionShort"
          value={formData.descriptionShort || ""}
          onChange={handleInputChange}
          className={inputStyle("descriptionShort")}
        />
        {errors.descriptionShort && (
          <p className="mt-1 text-sm text-red-600">
            {t("public_form.common.cargo_description_required")}
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 pt-4">
        <div>
          <label
            htmlFor="quantity"
            className="block text-sm font-medium text-secondary"
          >
            {t("public_form.common.pieces_count")}
          </label>
          <input
            type="number"
            name="quantity"
            id="quantity"
            value={formData.quantity || 1}
            onChange={handleInputChange}
            className={inputStyle("quantity")}
          />
        </div>
        <div>
          <label
            htmlFor="weight"
            className="block text-sm font-medium text-secondary"
          >
            {t("public_form.common.weight_kg")}
          </label>
          <input
            type="number"
            name="weight"
            id="weight"
            value={formData.weight || 0}
            onChange={handleInputChange}
            className={inputStyle("weight")}
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 pt-4">
        <div>
          <label
            htmlFor="length"
            className="block text-sm font-medium text-secondary"
          >
            {t("public_form.common.length_cm")}
          </label>
          <input
            type="number"
            name="length"
            id="length"
            value={formData.length || 0}
            onChange={handleInputChange}
            className={inputStyle("length")}
          />
        </div>
        <div>
          <label
            htmlFor="height"
            className="block text-sm font-medium text-secondary"
          >
            {t("public_form.common.height_cm")}
          </label>
          <input
            type="number"
            name="height"
            id="height"
            value={formData.height || 0}
            onChange={handleInputChange}
            className={inputStyle("height")}
          />
        </div>
        <div>
          <label
            htmlFor="width"
            className="block text-sm font-medium text-secondary"
          >
            {t("public_form.common.width_cm")}
          </label>
          <input
            type="number"
            name="width"
            id="width"
            value={formData.width || 0}
            onChange={handleInputChange}
            className={inputStyle("width")}
          />
        </div>
      </div>
    </form>
  );
};

export const PublicProductModal: FC<PublicProductModalProp> = (props) => {
  const { t } = useTranslation(["order_form", "common"]);

  return (
    <Dialog
      title={
        !props.data.id
          ? t("public_form.common.add_cargo")
          : t("public_form.common.edit_cargo")
      }
      show={props.isShowing}
      toggleModal={props.onCancel}
      buttonConfirmText={t("public_form.common.save")}
      buttonCloseText={t("common:close")}
      confirmButtonColor="primary"
      confirmButton={() => {
        document
          .getElementById("public-product-form")
          ?.dispatchEvent(
            new Event("submit", { cancelable: true, bubbles: true }),
          );
      }}
      content={<PublicProductForm {...props} />}
    />
  );
};

export default PublicProductModal;
